import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaFilter } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';


const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${day}-${month}-${year}`;
};


// Helper function to parse date string to Date object
const parseDate = (dateString) => {
  if (!dateString) return null;

  // Handle dd-mm-yyyy format (from filter inputs)
  if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [day, month, year] = dateString.split('-');
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle YYYY-MM-DD format (from database)
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const date = new Date(dateString + 'T00:00:00');
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle other formats
  let date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};


const WarehouseCostingReport = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);


  // Updated filter state with lifting date filters
  const [filters, setFilters] = useState({
    warehouseName: '',
    warehouseLocation: '',
    vesselName: '',
    commodity: '',
    origin: '',
    liftingDateFrom: '',
    liftingDateTo: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    warehouseNames: [],
    warehouseLocations: [],
    vesselNames: [],
    commodities: [],
    origins: []
  });
  const [showFilters, setShowFilters] = useState(false);


  useEffect(() => {











    axios
      .get(`${API_BASE_URL}/api/report/warehouse-costing-report`, { withCredentials: true })
      .then(res => {
        // Helper function to get date for sorting
        const getDateForSort = (item) => {
          if (item.type === 'Inward' || item.type === 'Transfer In') {
            return item.inward_date ? parseDate(item.inward_date) : null;
          } else if (item.type === 'Outward' || item.type === 'Transfer Out') {
            return item.outward_date ? parseDate(item.outward_date) : null;
          }
          return item.date ? parseDate(item.date) : null;
        };

        // ✅ Group data by Warehouse Name, Warehouse Location, Vessel Name, and Commodity
        const groupedData = new Map();
        
        res.data.forEach(item => {
          const groupKey = `${item.warehouse_name || ''}_${item.state || ''}_${item.vessel_name || ''}_${item.commodity || ''}`;

          if (!groupedData.has(groupKey)) {
            groupedData.set(groupKey, []);
          }
          groupedData.get(groupKey).push(item);
        });
        
        // ✅ Calculate running balance separately for each group
        const dataWithStock = [];
        
        groupedData.forEach((groupItems, groupKey) => {
          // Sort each group by date
          const sortedGroup = [...groupItems].sort((a, b) => {










































            const dateA = getDateForSort(a);
            const dateB = getDateForSort(b);

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            return dateA - dateB;
          });

          // Calculate opening and closing stock for this group (starts from 0 for each group)
          let closingStock = 0;
          const groupWithStock = sortedGroup.map((row) => {
            const currentOpeningStock = closingStock;
            const quantity = parseFloat(row.quantity) || 0;
            
            let inwardQty = 0;
            let outwardQty = 0;
            
            if (row.type === 'Inward' || row.type === 'Transfer In' || row.type === 'Adjustment (Excess)') {
              inwardQty = quantity;
            } else if (row.type === 'Outward' || row.type === 'Transfer Out' || row.type === 'Adjustment (Shortage)') {
              outwardQty = quantity;
            }
            
            closingStock = currentOpeningStock + inwardQty - outwardQty;
            
            return {
              ...row,
              openingStock: parseFloat(currentOpeningStock.toFixed(3)),
              closingStock: parseFloat(closingStock.toFixed(3))
            };
          });
          
          dataWithStock.push(...groupWithStock);
        });

        // Sort all data by date for final display
        dataWithStock.sort((a, b) => {
          const dateA = getDateForSort(a);
          const dateB = getDateForSort(b);
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return dateA - dateB;
        });

        setData(dataWithStock);
        setFilteredData(dataWithStock);


        // Extract unique filter options
        const warehouseNames = [...new Set(dataWithStock.map(item => item.warehouse_name))];
        const locations = [...new Set(dataWithStock.map(item => item.state))];
        const vessels = [...new Set(dataWithStock.map(item => item.vessel_name))];
        const commodities = [...new Set(dataWithStock.map(item => item.commodity))];
        const origins = [...new Set(dataWithStock.map(item => item.origin))];

        setFilterOptions({
          warehouseNames: warehouseNames,
          warehouseLocations: locations,
          vesselNames: vessels,
          commodities: commodities,
          origins: origins
        });
      })
      .catch(err => console.error(err));
  }, []);


  // ✅ UPDATED FILTERING LOGIC WITH FIXED OPENING STOCK CALCULATION
  useEffect(() => {
  const filtered = data.filter(item => {
    const matchesWarehouseName = filters.warehouseName === '' || item.warehouse_name === filters.warehouseName;
    const matchesWarehouseLocation = filters.warehouseLocation === '' || item.state === filters.warehouseLocation;
    const matchesVesselName = filters.vesselName === '' || item.vessel_name === filters.vesselName;
    const matchesCommodity = filters.commodity === '' || item.commodity === filters.commodity;
    const matchesOrigin = filters.origin === '' || item.origin === filters.origin;

    let matchesDateRange = true;
    if (filters.liftingDateFrom || filters.liftingDateTo) {
      let recordDate = null;
      if (item.type === 'Inward' || item.type === 'Transfer In' || item.type === 'Adjustment (Excess)') {
        recordDate = parseDate(item.inward_date);
      } else if (item.type === 'Outward' || item.type === 'Transfer Out' || item.type === 'Adjustment (Shortage)') {
        recordDate = parseDate(item.outward_date);
      }
      
      if (recordDate) {
        if (filters.liftingDateFrom && filters.liftingDateTo) {
          const fromDate = parseDate(filters.liftingDateFrom);
          const toDate = parseDate(filters.liftingDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
          }
          matchesDateRange = fromDate && toDate && recordDate >= fromDate && recordDate <= toDate;
        } else if (filters.liftingDateTo) {
          const toDate = parseDate(filters.liftingDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            matchesDateRange = recordDate <= toDate;
          }
        } else if (filters.liftingDateFrom) {
          const fromDate = parseDate(filters.liftingDateFrom);
          matchesDateRange = fromDate && recordDate >= fromDate;
        }
      } else {
        matchesDateRange = false;
      }
    }

    return matchesWarehouseName && matchesWarehouseLocation && matchesVesselName && 
           matchesCommodity && matchesOrigin && matchesDateRange;
  });
  
  // Helper function to get date for sorting
  const getDateForSort = (item) => {
    if (item.type === 'Inward' || item.type === 'Transfer In') {
      return item.inward_date ? parseDate(item.inward_date) : null;
    } else if (item.type === 'Outward' || item.type === 'Transfer Out') {
      return item.outward_date ? parseDate(item.outward_date) : null;
    }
    return item.date ? parseDate(item.date) : null;
  };
  
  // ✅ Group filtered data by Warehouse Name, Warehouse Location, Vessel Name, and Commodity
  const groupedFiltered = new Map();
  
  filtered.forEach(item => {
    const groupKey = `${item.warehouse_name || ''}_${item.state || ''}_${item.vessel_name || ''}_${item.commodity || ''}`;
    
    if (!groupedFiltered.has(groupKey)) {
      groupedFiltered.set(groupKey, []);
    }
    groupedFiltered.get(groupKey).push(item);
  });
  
  // ✅ Calculate running balance separately for each group
  const filteredDataWithStock = [];
  
  groupedFiltered.forEach((groupItems, groupKey) => {
    // Sort each group by date
    const sortedGroup = [...groupItems].sort((a, b) => {
      const dateA = getDateForSort(a);
      const dateB = getDateForSort(b);
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      return dateA - dateB;
    });

    // Calculate opening stock for this group if date filter is applied
    let groupOpeningStock = 0;
    
    if (sortedGroup.length > 0 && filters.liftingDateFrom) {
      const filterDate = parseDate(filters.liftingDateFrom);
      
      if (filterDate) {
        filterDate.setHours(0, 0, 0, 0);
        
        const getDateForRow = (item) => {
          if (item.type === 'Inward' || item.type === 'Transfer In' || item.type === 'Adjustment (Excess)') {
            return item.inward_date ? parseDate(item.inward_date) : null;
          } else if (item.type === 'Outward' || item.type === 'Transfer Out' || item.type === 'Adjustment (Shortage)') {
            return item.outward_date ? parseDate(item.outward_date) : null;
          }
          return item.date ? parseDate(item.date) : null;
        };
        
        // Get transactions before filter date for THIS SPECIFIC GROUP
        // Extract group components from the first item in the group
        const firstGroupItem = sortedGroup[0];
        const groupWarehouseName = firstGroupItem.warehouse_name || '';
        const groupState = firstGroupItem.state || '';
        const groupVesselName = firstGroupItem.vessel_name || '';
        const groupCommodity = firstGroupItem.commodity || '';
        
        const transactionsBeforeFilter = data.filter(item => {
          // Match exact group combination
          const matchesGroup = 
            (item.warehouse_name || '') === groupWarehouseName &&
            (item.state || '') === groupState &&
            (item.vessel_name || '') === groupVesselName &&
            (item.commodity || '') === groupCommodity;
          
          if (!matchesGroup) return false;
          
          // Apply other filters (origin filter)
          const matchesOrigin = filters.origin === '' || item.origin === filters.origin;
          if (!matchesOrigin) return false;
          
          const itemDate = getDateForRow(item);
          if (!itemDate) return false;
          
          const normalizedItemDate = new Date(itemDate);
          normalizedItemDate.setHours(0, 0, 0, 0);
          
          return normalizedItemDate < filterDate;
        });
        
        // Sort chronologically and calculate opening stock for this group
        transactionsBeforeFilter.sort((a, b) => {
          const dateA = getDateForRow(a);
          const dateB = getDateForRow(b);
          if (!dateA || !dateB) return 0;
          return dateA - dateB;
        });
        
        // Calculate opening stock by summing all inward minus all outward for this group
        transactionsBeforeFilter.forEach(transaction => {
          const quantity = parseFloat(transaction.quantity) || 0;
          
          if (transaction.type === 'Inward' || transaction.type === 'Transfer In' || transaction.type === 'Adjustment (Excess)') {
            groupOpeningStock += quantity;
          } else if (transaction.type === 'Outward' || transaction.type === 'Transfer Out' || transaction.type === 'Adjustment (Shortage)') {
            groupOpeningStock -= quantity;
          }
        });
      }
    } else if (sortedGroup.length > 0) {
      // If no date filter, use the first row's opening stock from original data
      groupOpeningStock = sortedGroup[0].openingStock || 0;
    }
    
    // Calculate running balance for this group (starts from groupOpeningStock)
    let runningClosingStock = groupOpeningStock;
    const groupWithStock = sortedGroup.map((row) => {
      const currentOpeningStock = runningClosingStock;
      const quantity = parseFloat(row.quantity) || 0;
      
      let inwardQty = 0;
      let outwardQty = 0;
      
      if (row.type === 'Inward' || row.type === 'Transfer In' || row.type === 'Adjustment (Excess)') {
        inwardQty = quantity;
      } else if (row.type === 'Outward' || row.type === 'Transfer Out' || row.type === 'Adjustment (Shortage)') {
        outwardQty = quantity;
      }
      
      runningClosingStock = currentOpeningStock + inwardQty - outwardQty;
      
      return {
        ...row,
        openingStock: parseFloat(currentOpeningStock.toFixed(3)),
        closingStock: parseFloat(runningClosingStock.toFixed(3))
      };
    });
    
    filteredDataWithStock.push(...groupWithStock);
  });
  
  // Sort all data by date for final display
  filteredDataWithStock.sort((a, b) => {
    const dateA = getDateForSort(a);
    const dateB = getDateForSort(b);
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return dateA - dateB;
  });
  
  setFilteredData(filteredDataWithStock);
}, [filters, data]);


  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const resetFilters = () => {
    setFilters({
      warehouseLocation: '',
      vesselName: '',
      commodity: '',
      origin: '',
      warehouseName: '',
      liftingDateFrom: '',
      liftingDateTo: ''
    });
  };


  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };


  // Calculate subtotals

  const calculateSubtotals = () => {
    let inwardTotal = 0;
    let outwardTotal = 0;

    filteredData.forEach(row => {
      const quantity = Number(row.quantity) || 0;
      if (row.type === 'Inward' || row.type === 'Transfer In' || row.type === 'Adjustment (Excess)') {

        inwardTotal += quantity;
      } else if (row.type === 'Outward' || row.type === 'Transfer Out' || row.type === 'Adjustment (Shortage)') {
        outwardTotal += quantity;
      }

    });






    return {
      inwardTotal: inwardTotal.toFixed(3),
      outwardTotal: outwardTotal.toFixed(3),
      balanceTotal: (inwardTotal - outwardTotal).toFixed(3)


    };
  };

  const { inwardTotal, outwardTotal, balanceTotal } = calculateSubtotals();

  // Get final closing stock and initial opening stock from filtered data
  const getStockValues = () => {
    if (filteredData.length === 0) {
      return { finalClosingStock: 0, initialOpeningStock: 0 };
    }
    
    const lastRow = filteredData[filteredData.length - 1];
    const finalClosingStock = lastRow.closingStock || 0;
    
    const firstRow = filteredData[0];
    const initialOpeningStock = firstRow.openingStock || 0;
    
    return { finalClosingStock, initialOpeningStock };
  };

  const { finalClosingStock, initialOpeningStock } = getStockValues();

  const dataWithClosingStock = filteredData;


  return (
    <div>
      <div className="bg-white shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
          Warehouse Report Details
          </h2>
          <button
            onClick={toggleFilters}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-[#3899cf] rounded-md hover:bg-blue-200 transition"
          >
            <FaFilter />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex flex-wrap gap-4">
              {/* Warehouse Name */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Name</label>
                <select
                  name="warehouseName"
                  value={filters.warehouseName}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Warehouses</option>
                  {filterOptions.warehouseNames.map((name, index) => (
                    <option key={index} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Warehouse Location */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Location</label>
                <select
                  name="warehouseLocation"
                  value={filters.warehouseLocation}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Locations</option>
                  {filterOptions.warehouseLocations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              {/* Lifting Date From */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifting Date From</label>
                <input
                  type="date"
                  name="liftingDateFrom"
                  value={filters.liftingDateFrom}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Lifting Date To */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Lifting Date To</label>
                <input
                  type="date"
                  name="liftingDateTo"
                  value={filters.liftingDateTo}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Vessel Name */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Name</label>
                <select
                  name="vesselName"
                  value={filters.vesselName}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Vessels</option>
                  {filterOptions.vesselNames.map((vessel, index) => (
                    <option key={index} value={vessel}>{vessel}</option>
                  ))}
                </select>
              </div>

              {/* Commodity */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Commodity</label>
                <select
                  name="commodity"
                  value={filters.commodity}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Commodities</option>
                  {filterOptions.commodities.map((commodity, index) => (
                    <option key={index} value={commodity}>{commodity}</option>
                  ))}
                </select>
              </div>

              {/* Origin */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Origin</label>
                <select
                  name="origin"
                  value={filters.origin}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Origins</option>
                  {filterOptions.origins.map((origin, index) => (
                    <option key={index} value={origin}>{origin}</option>
                  ))}
                </select>
              </div>

              {/* Reset Button */}
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-200 rounded">
            <thead className="bg-blue-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Warehouse Name</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Warehouse Location</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Warehouse Type</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Vessel Name</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Commodity</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Origin</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Inward Date</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Outward Date</th>
                <th className="px-4 py-3 text-left text-gray-700 font-medium border">Type</th>
                <th className="px-4 py-3 text-right text-gray-700 font-medium border">Opening Stock (MT)</th>
                <th className="px-4 py-3 text-center text-gray-700 font-medium border">Inward Qty (MT)</th>
                <th className="px-4 py-3 text-center text-gray-700 font-medium border">Outward Qty (MT)</th>
                <th className="px-4 py-3 text-center text-gray-700 font-medium border">Closing Stock (MT)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.length > 0 ? (
                <>
                  {dataWithClosingStock.map((row, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition duration-200 ease-in-out">
                      <td className="px-4 py-2 border font-medium text-gray-800">
                        {row.warehouse_name}
                      </td>
                      <td className="px-4 py-2 border text-gray-600">
                        {row.state}
                      </td>
                      <td className="px-4 py-2 border text-gray-700">
                        {row.warehouse_type}
                      </td>
                      <td className="px-4 py-2 border text-gray-700">
                        {row.vessel_name}
                      </td>
                      <td className="px-4 py-2 border text-gray-700">
                        {row.commodity} 
                      </td>
                      <td className="px-4 py-2 border text-gray-600">
                        {row.origin}
                      </td>
                      <td className="px-4 py-2 border text-gray-600">
                        {row.inward_date ? formatDate(row.inward_date) : '-'}
                      </td>
                      <td className="px-4 py-2 border text-gray-600">
                        {row.outward_date ? formatDate(row.outward_date) : '-'}
                      </td>
                      <td className="px-4 py-2 border text-gray-700">
                        {row.type}
                      </td>
                      <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                        {row.openingStock !== undefined && row.openingStock !== null ? Number(row.openingStock).toFixed(3) : '0.000'}
                      </td>
                      <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                        {(row.type === 'Inward' || row.type === 'Transfer In' || row.type === 'Adjustment (Excess)') ? 
                          Number(row.quantity).toFixed(3) : '-'}
                      </td>
                      <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                        {(row.type === 'Outward' || row.type === 'Transfer Out' || row.type === 'Adjustment (Shortage)') ? 
                          Number(row.quantity).toFixed(3) : '-'}
                      </td>
                      <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                        {row.closingStock !== undefined && row.closingStock !== null ? Number(row.closingStock).toFixed(3) : '0.000'}
                      </td>
                    </tr>
                  ))}
                  {/* Subtotals row */}
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan="6" className="px-4 py-3 border text-right text-gray-700">
                      Subtotal:
                    </td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {Number(initialOpeningStock).toFixed(3)}
                    </td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {inwardTotal}
                    </td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {outwardTotal}
                    </td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {Number(finalClosingStock).toFixed(3)}
                    </td>
                  </tr>
                  {/* Balance row */}
                  <tr className="bg-blue-50 font-bold">
                    <td colSpan="6" className="px-4 py-3 border text-right text-gray-700">
                      Balance:
                    </td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 border text-gray-700"></td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {Number(initialOpeningStock).toFixed(3)}
                    </td>
                    <td colSpan="2" className="px-4 py-3 text-right border text-gray-800">
                      {balanceTotal}
                    </td>
                    <td className="px-4 py-3 text-right border text-gray-800">
                      {Number(finalClosingStock).toFixed(3)}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan="13" className="text-center p-8 text-gray-500 italic">
                    {data.length === 0 ? 'No warehouse transaction records found.' : 'No records match the selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default WarehouseCostingReport;